import {useMemo} from 'react'
import {useMutation, useQuery, useQueryClient} from 'react-query'
import {useSelector} from 'react-redux'

import {API} from '../../networking/http'
import {AuthService} from '../../services/auth'
import {cardUiState} from './card-state'
import {ClaimView, MyPromos, PromoCardData} from './types'

export const myPromosKey = 'promo-codes-mine'

const EMPTY: MyPromos = {ready: [], used: [], expired: []}

export const fetchMyPromos = (venueId?: string) =>
  API.get<undefined, Partial<MyPromos>>('promo-codes/mine', {params: venueId ? {venueId} : undefined}).then((res) => ({
    ready: res.data.ready ?? [],
    used: res.data.used ?? [],
    expired: res.data.expired ?? [],
  }))

// The signed-in customer's cards for this venue. Signed out: nothing is fetched.
export const useMyPromos = (venueId?: string) => {
  const isLoggedIn = useSelector(AuthService.selectors.isLoggedIn())
  const query = useQuery([myPromosKey, venueId ?? null], () => fetchMyPromos(venueId), {
    enabled: isLoggedIn && Boolean(venueId),
    staleTime: 30_000,
    retry: false,
    refetchOnWindowFocus: false,
  })
  return {...query, data: query.data ?? EMPTY}
}

// The venue's public shared codes (the ones it shows on its page). Anyone may use them, signed in or not.
export const useVenueOffers = (venueId?: string): PromoCardData[] => {
  const {data} = useQuery(
    ['promo-codes-offers', venueId ?? null],
    () =>
      API.get<undefined, {items?: PromoCardData[]}>('promo-codes/offers', {params: {venueId}}).then(
        (res) => res.data.items ?? [],
      ),
    {enabled: Boolean(venueId), staleTime: 60_000, retry: false, refetchOnWindowFocus: false},
  )
  return data ?? []
}

// Ready cards only; a quiet failure just means no chips.
export const useReadyPromos = (venueId?: string): PromoCardData[] => {
  const {data} = useMyPromos(venueId)
  return useMemo(() => data.ready.filter((c) => cardUiState(c, 'ready') === 'ready'), [data.ready])
}

export const useClaim = (token?: string) =>
  useQuery(
    ['promo-codes-claim', token],
    () => API.get<undefined, ClaimView>('promo-codes/by-token', {params: {token}}).then((res) => res.data),
    {enabled: Boolean(token), retry: false, refetchOnWindowFocus: false},
  )

export const useClaimPromo = () => {
  const queryClient = useQueryClient()
  return useMutation(
    (token: string) =>
      API.post<{token: string}, {copy: PromoCardData}>('promo-codes/claim', {token}).then((res) => res.data.copy),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries(myPromosKey)
        void queryClient.invalidateQueries('promo-codes-claim')
      },
    },
  )
}

// "Got a code? Add it." Refusals come back as the usual 422 `{errors: [PromoError]}`.
export const useAddCode = () => {
  const queryClient = useQueryClient()
  return useMutation(
    (body: {venueId: string; code: string}) =>
      API.post<typeof body, {copy?: PromoCardData}>('promo-codes/claim-by-code', body).then((res) => res.data),
    {onSuccess: () => void queryClient.invalidateQueries(myPromosKey)},
  )
}
